import React from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  Table,
  Button,
  Upload,
  Form,
  Input,
  Switch,
  Space,
  Popconfirm,
  Tag,
  message,
  Divider,
  Row,
  Col,
  Card,
  Statistic,
  Tooltip
} from 'antd'
import {
  UploadOutlined,
  FileTextOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
import PdfPlaceholderEditor from '../../../../Common/PdfPlaceholderEditor'
import {
  getTbtDocumentPdfInfo,
  getTbtDocumentPdfContent,
  saveTbtPlaceholderConfig,
  previewTbtPlaceholders,
} from '../../../../HttpRequests/toolboxTalks'

const { TextArea } = Input

export default class DocumentsModal extends React.PureComponent {
  static propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    documents: PropTypes.array,
    loading: PropTypes.bool,
    uploading: PropTypes.bool,
    statistics: PropTypes.object,
    onUpload: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onToggleActive: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
  }

  static defaultProps = {
    documents: [],
    loading: false,
    uploading: false,
    statistics: {}
  }

  state = {
    uploadForm: {
      title: '',
      description: '',
      active: true
    },
    fileList: [],
    showUploadForm: false,
    // Placeholder editor state
    editorVisible: false,
    editorDocumentId: null,
    editorDocumentTitle: '',
    editorLoading: false,
    editorSaving: false,
    editorPdfUrl: null,
    editorPages: [],
    editorPlaceholderConfig: null,
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({
        editorVisible: false,
        editorDocumentId: null,
        editorDocumentTitle: '',
        editorPdfUrl: null,
        editorPdfData: null,
        editorPages: [],
        editorPlaceholderConfig: null,
      })
    }
  }

  handleUploadFormChange = (field, value) => {
    this.setState({
      uploadForm: {
        ...this.state.uploadForm,
        [field]: value
      }
    })
  }

  handleFileChange = ({ fileList }) => {
    this.setState({ fileList })
  }

  handleUpload = () => {
    const { uploadForm, fileList } = this.state
    const { onUpload, onRefresh } = this.props

    if (!uploadForm.title.trim()) {
      message.error('Please enter a document title')
      return
    }

    if (fileList.length === 0) {
      message.error('Please select a file to upload')
      return
    }

    const formData = new FormData()
    formData.append('title', uploadForm.title)
    formData.append('description', uploadForm.description)
    formData.append('active', uploadForm.active ? '1' : '0')
    formData.append('document', fileList[0].originFileObj)

    onUpload(formData).then(() => {
      this.setState({
        uploadForm: { title: '', description: '', active: true },
        fileList: [],
        showUploadForm: false
      })
      message.success('Document uploaded successfully')
      // Refresh both documents and statistics after successful upload
      if (onRefresh) {
        onRefresh()
      }
    }).catch(() => {
      message.error('Failed to upload document')
    })
  }

  handleDelete = (id, title) => {
    const { onDelete, onRefresh } = this.props
    onDelete(id).then(() => {
      message.success(`Document "${title}" deleted successfully`)
      // Refresh both documents and statistics after successful delete
      if (onRefresh) {
        onRefresh()
      }
    }).catch(() => {
      message.error('Failed to delete document')
    })
  }

  handleToggleActive = (id, currentStatus) => {
    const { onToggleActive, onRefresh } = this.props
    onToggleActive(id).then(() => {
      message.success(`Document ${currentStatus ? 'deactivated' : 'activated'} successfully`)
      // Refresh both documents and statistics after successful status change
      if (onRefresh) {
        onRefresh()
      }
    }).catch(() => {
      message.error('Failed to update document status')
    })
  }

  handleOpenEditor = (record) => {
    var self = this
    this.setState({
      editorVisible: true,
      editorDocumentId: record.id,
      editorDocumentTitle: record.title,
      editorLoading: true,
      editorPdfUrl: null,
      editorPdfData: null,
      editorPages: [],
      editorPlaceholderConfig: null,
    })

    // Fetch PDF info and content in parallel (content proxied through backend to avoid CORS)
    Promise.all([
      getTbtDocumentPdfInfo(record.id),
      getTbtDocumentPdfContent(record.id),
    ]).then(function(results) {
      var infoResponse = results[0]
      var contentResponse = results[1]
      self.setState({
        editorLoading: false,
        editorPages: (infoResponse.data && infoResponse.data.pages) || [],
        editorPlaceholderConfig: (infoResponse.data && infoResponse.data.placeholder_config) || { placeholders: [] },
        editorPdfData: contentResponse.data || null,
      })
    }).catch(function() {
      self.setState({ editorLoading: false })
      message.error('Failed to load PDF information')
    })
  }

  handleCloseEditor = () => {
    this.setState({
      editorVisible: false,
      editorDocumentId: null,
      editorDocumentTitle: '',
      editorPdfUrl: null,
      editorPages: [],
      editorPlaceholderConfig: null,
    })
    if (this.props.onRefresh) {
      this.props.onRefresh()
    }
  }

  handleSavePlaceholderConfig = (config) => {
    const documentId = this.state.editorDocumentId
    this.setState({ editorSaving: true })

    saveTbtPlaceholderConfig(documentId, config)
      .then(() => {
        this.setState({
          editorSaving: false,
          editorPlaceholderConfig: config,
        })
        message.success('Field configuration saved')
      })
      .catch(() => {
        this.setState({ editorSaving: false })
        message.error('Failed to save field configuration')
      })
  }

  handlePreviewPlaceholders = () => {
    const documentId = this.state.editorDocumentId
    previewTbtPlaceholders(documentId)
      .then((response) => {
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
      })
      .catch(() => {
        message.error('Failed to generate preview')
      })
  }

  getColumns = () => [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <Space>
          <FileTextOutlined />
          <span>{title}</span>
          {!record.active && <Tag color="red">Inactive</Tag>}
        </Space>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description) => description || <span style={{ color: '#999' }}>No description</span>
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Signed',
      dataIndex: 'signed_count',
      key: 'signed_count',
      width: 80,
      render: (count) => count || 0
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Fields',
      key: 'placeholders',
      width: 90,
      render: (_, record) => {
        const config = record.placeholder_config
        const count = (config && config.placeholders) ? config.placeholders.length : 0
        return count > 0
          ? <Tag color="blue">{count}</Tag>
          : <Tag>None</Tag>
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Fields">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => this.handleOpenEditor(record)}
            />
          </Tooltip>
          <Tooltip title="View Document">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                const teamSlug = window.location.pathname.split('/')[3] // Extract team from URL
                window.open(`/exports/teams/${teamSlug}/toolbox-talks/${record.id}/view`, '_blank')
              }}
            />
          </Tooltip>
          <Tooltip title="Download Document">
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => {
                const teamSlug = window.location.pathname.split('/')[3] // Extract team from URL
                const link = document.createElement('a')
                link.href = `/exports/teams/${teamSlug}/toolbox-talks/${record.id}/download`
                link.download = `${record.title}.${record.file_type}`
                link.click()
              }}
            />
          </Tooltip>
          <Tooltip title={record.active ? 'Deactivate Document' : 'Activate Document'}>
            <Button
              type="link"
              size="small"
              icon={record.active ? <StopOutlined /> : <CheckCircleOutlined />}
              onClick={() => this.handleToggleActive(record.id, record.active)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this document?"
            onConfirm={() => this.handleDelete(record.id, record.title)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Document">
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ]

  renderPlaceholderEditor = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #e8e8e8', display: 'flex', alignItems: 'center' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={this.handleCloseEditor}
            size="small"
          >
            Back to Documents
          </Button>
          <span style={{ marginLeft: 12, fontWeight: 'bold' }}>
            Editing Fields: {this.state.editorDocumentTitle}
          </span>
        </div>

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <PdfPlaceholderEditor
            pdfData={this.state.editorPdfData}
            pages={this.state.editorPages}
            placeholderConfig={this.state.editorPlaceholderConfig}
            onSave={this.handleSavePlaceholderConfig}
            onPreview={this.handlePreviewPlaceholders}
            loading={this.state.editorLoading}
            saving={this.state.editorSaving}
          />
        </div>
      </div>
    )
  }

  render() {
    const { visible, onClose, documents, loading, uploading, statistics } = this.props
    const { uploadForm, fileList, showUploadForm, editorVisible } = this.state
    const modalTitle = editorVisible ? 'Edit Fields' : 'Manage TBT Documents'

    var modalWidth = editorVisible ? '100%' : 1200
    var bodyStyle = editorVisible
      ? { padding: 0, height: 'calc(100vh - 160px)', overflow: 'hidden' }
      : { maxHeight: '80vh', overflowY: 'auto' }

    return (
      <Modal
        title={modalTitle}
        visible={visible}
        onCancel={onClose}
        width={modalWidth}
        footer={null}
        destroyOnClose
        bodyStyle={bodyStyle}
        style={editorVisible ? { top: 20 } : undefined}
      >
        {editorVisible ? this.renderPlaceholderEditor() : (
        <div>
        {/* Statistics */}
        {statistics && Object.keys(statistics).length > 0 && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Documents"
                    value={statistics.total_documents || 0}
                    prefix={<FileTextOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Active Documents"
                    value={statistics.active_documents || 0}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Signatures"
                    value={statistics.recent_signatures || 0}
                    valueStyle={{ color: '#1890ff' }}
                    suffix="/ 30 days"
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Runs"
                    value={statistics.recent_runs || 0}
                    suffix="/ 30 days"
                  />
                </Card>
              </Col>
            </Row>
            <Divider />
          </div>
        )}

        {/* Upload Section */}
        <div style={{ marginBottom: 24 }}>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => this.setState({ showUploadForm: !showUploadForm })}
          >
            {showUploadForm ? 'Cancel Upload' : 'Upload New Document'}
          </Button>
        </div>

        {showUploadForm && (
          <Card style={{ marginBottom: 24 }}>
            <Form layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Document Title" required>
                    <Input
                      value={uploadForm.title}
                      onChange={(e) => this.handleUploadFormChange('title', e.target.value)}
                      placeholder="Enter document title"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Active">
                    <Switch
                      checked={uploadForm.active}
                      onChange={(checked) => this.handleUploadFormChange('active', checked)}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Description">
                <TextArea
                  value={uploadForm.description}
                  onChange={(e) => this.handleUploadFormChange('description', e.target.value)}
                  placeholder="Enter document description (optional)"
                  rows={3}
                />
              </Form.Item>
              <Form.Item label="Document File" required>
                <Upload
                  fileList={fileList}
                  onChange={this.handleFileChange}
                  beforeUpload={() => false}
                  accept=".pdf,.doc,.docx"
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>
                <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                  Supported formats: PDF, DOC, DOCX (Max 10MB). Word documents will be converted to PDF.
                </div>
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    onClick={this.handleUpload}
                    loading={uploading}
                  >
                    Upload Document
                  </Button>
                  <Button onClick={() => this.setState({ showUploadForm: false })}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* Documents Table */}
        <Table
          columns={this.getColumns()}
          dataSource={documents}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} documents`
          }}
        />
        </div>
        )}
      </Modal>
    )
  }
}
